import { useCurrentUser } from './useCurrentUser';

/**
 * The REAL, backend-enforced Insights permission codes (already in
 * permissionCatalog.ts and in server/src/config/permissionMap.ts, where they
 * gate the whole /api/analytics family). The frontend-only role mock these used
 * to come from is gone.
 */
export const INSIGHTS_CLIENT = 'insights_client' as const;
export const INSIGHTS_MANAGER = 'insights_manager' as const;

/**
 * THE canonical "insights-only persona" test: the user holds an Insights
 * permission and NOTHING else beyond the neutral `default` baseline. Such a user
 * gets a locked-down surface (only the Insights tab; lands on Insights). A mixed
 * role (e.g. Client Success, which also holds `flex`) is NOT insights-only. Pure
 * function so the sidebar gate and the landing redirect share one definition.
 */
export function isInsightsOnlyPersona(permissions: readonly string[]): boolean {
  const hasInsights = permissions.includes(INSIGHTS_CLIENT) || permissions.includes(INSIGHTS_MANAGER);
  return (
    hasInsights &&
    permissions.every((p) => p === 'default' || p === INSIGHTS_CLIENT || p === INSIGHTS_MANAGER)
  );
}

export interface InsightsAccess {
  /** Insights tab + Reports sub-tab (insights_client OR insights_manager). */
  canView: boolean;
  /** Manage sub-tab (insights_manager). Managers are also region-unrestricted. */
  canManage: boolean;
  /**
   * Locked-down persona — insights permission only (no other capability). Drives
   * the "only Insights tab" sidebar and the "land on Insights" redirect. Always
   * false for super-admin. See isInsightsOnlyPersona.
   */
  isInsightsOnly: boolean;
  /**
   * Whether the viewer's region zones can constrain what they see. TRUE only for
   * a client-alone viewer; a manager (or super-admin) is ALWAYS unrestricted.
   * Presentation bookkeeping only — the ACTUAL gate is server-side
   * (GET /api/analytics/client-reports/:id/{regions,content} resolve the zone
   * rows before returning any slot or any bytes).
   */
  zonesApply: boolean;
  isLoading: boolean;
}

/**
 * THE canonical Insights access resolver — the one source every Insights UI gate
 * (sidebar entry, page, sub-tabs) reads, now backed by real RBAC v2 permissions
 * from /api/me rather than a client-side role mock:
 *   - canManage = super-admin || insights_manager
 *   - canView   = canManage || insights_client
 *   - zonesApply = view-from-client-alone
 * Super-admin retains the platform-wide unrestricted invariant honored elsewhere
 * (useSidebarNav.includeAdmin, RequireRole) — `hasPermission` already bypasses
 * for super-admin, so the explicit isSuperAdmin term is belt-and-braces for the
 * RBAC_V2-off case.
 *
 * NOT A SECURITY BOUNDARY: this only gates which tab/sub-tabs render. The report
 * routes enforce insights_manager / insights_client and per-region zones
 * server-side.
 */
export function useInsightsAccess(): InsightsAccess {
  const { isSuperAdmin, hasPermission, permissions, isLoading } = useCurrentUser();
  const canManage = isSuperAdmin || hasPermission(INSIGHTS_MANAGER);
  const canView = canManage || hasPermission(INSIGHTS_CLIENT);
  const zonesApply = canView && !canManage;
  const isInsightsOnly = !isSuperAdmin && isInsightsOnlyPersona(permissions);
  return { canView, canManage, isInsightsOnly, zonesApply, isLoading };
}
